import { useEffect, useRef } from 'react';
import { BETZONE_KEY, BETZONE_VIEW } from 'config/constants';

const SCRIPT_URL = 'https://zone-widget.betapilive.com/embed.js';

interface BetZoneInstance {
  destroy: () => void;
}

declare global {
  interface Window {
    BetZoneEmbed?: {
      init: (o: Record<string, unknown>) => BetZoneInstance | null;
    };
  }
}

let loading: Promise<void> | null = null;

/** embed.js once per page; it puts BetZoneEmbed on window. */
const loadScript = () => {
  if (window.BetZoneEmbed) return Promise.resolve();
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_URL;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        loading = null;
        s.remove();
        reject(new Error('BetZone embed.js not loaded'));
      };
      document.head.appendChild(s);
    });
  }
  return loading;
};

interface Props {
  gameid: number;
  sport: number;
  home?: string;
  away?: string;
  /** Called when the widget cannot be loaded: show our own pitch. */
  onFail?: () => void;
}

/** The BetZone live tracker (an iframe its embed.js puts in the box). */
export const BetZoneTracker = ({
  gameid,
  sport,
  home,
  away,
  onFail,
}: Props) => {
  const box = useRef<HTMLDivElement>(null);
  const failRef = useRef(onFail);
  failRef.current = onFail;

  useEffect(() => {
    let alive = true;
    let widget: BetZoneInstance | null = null;
    loadScript()
      .then(() => {
        if (!alive || !box.current || !window.BetZoneEmbed) return;
        widget = window.BetZoneEmbed.init({
          container: box.current,
          key: BETZONE_KEY,
          gameid,
          sport,
          lang: 'en',
          height: 360,
          mobile: window.matchMedia('(max-width: 768px)').matches,
          // Our names, home always on the left as in our scoreboard.
          home,
          away,
          fixSides: true,
        });
        if (!widget) {
          failRef.current?.();
          return;
        }
        // embed.js does not pass `view`/`flags` on; the widget reads them
        // from its own address, so they are added there.
        const frame = box.current.querySelector('iframe');
        if (frame) {
          const u = new URL(frame.src);
          u.searchParams.set('view', BETZONE_VIEW);
          // The widget turns 3D on only with its flags: view3d (start in 3D)
          // and view3dui (its 2D/3D button, the player's pick remembered).
          if (BETZONE_VIEW === '3d') {
            u.searchParams.set('flags', 'view3d,view3dui');
          }
          frame.src = u.toString();
        }
      })
      .catch(() => alive && failRef.current?.());
    return () => {
      alive = false;
      widget?.destroy();
    };
  }, [gameid, sport, home, away]);

  return <div ref={box} style={{ width: '100%', minHeight: 28 }} />;
};
